import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CodeIcon from '@mui/icons-material/Code';
import ImageIcon from '@mui/icons-material/Image';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { AbilityContext } from '../../config/ability-context';
import EmailTemplatePlaceholder from '../../components/emailConfig/EmailTemplatePlaceholder';
import EmailSettingsLayout from '../../components/email-settings-layout';
import {
  DynamicLayoutFields,
  EmailDesignerPreview,
  HtmlCodeEditor,
  HtmlRequirements,
  LayoutModeSelector,
  PredefinedLayoutGallery,
  validateHtmlDocument,
} from '../../components/email-template-designer';
import {
  EmailSettingsFormSkeleton,
  EmailSettingsPageHeader,
  EmailSettingsPanel,
} from '../../components/email-settings-modern';
import * as Service from '../../services/emailtemplate.service';

const LAYOUT_MODE = { predefined: 1, customHtml: 2 };
const DEFAULT_LAYOUT_ID = 2;
const escapeHtmlAttribute = value => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');
const SAMPLE_BODY = '<p>Olá, <strong>{{ticket.requester.name}}</strong>!</p><p>O chamado <strong>#{{ticket.id}}</strong> recebeu uma atualização.</p>';
const CUSTOM_HTML_START = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>Atualização do chamado</title>
    <style>
      @media only screen and (max-width: 640px) {
        .email-container { width: 100% !important; }
      }
    </style>
  </head>
  <body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
      <tr>
        <td align="center">
          <table class="email-container" role="presentation" width="640" cellspacing="0" cellpadding="0" style="background:#ffffff;">
            <tr>
              <td style="padding:32px;">
                <h1>Olá, {{ticket.requester.name}}!</h1>
                <p>O chamado <strong>#{{ticket.id}}</strong> recebeu uma atualização.</p>
                <p><a href="{{ticket.url}}">Acessar chamado</a></p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;

const EMPTY_TEMPLATE = {
  id: 0,
  name: '',
  subject: 'Atualização do chamado #{{ticket.id}}',
  bodyhtml: SAMPLE_BODY,
  layoutmode: LAYOUT_MODE.predefined,
  predefinedlayoutid: DEFAULT_LAYOUT_ID,
  layoutdata: {},
  active: true,
  isdraft: false,
};

const MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link', 'image'];

const replaceAssetSources = (html, assets, useTemporaryUrl) => {
  if (!html || typeof DOMParser === 'undefined') return html || '';
  const document = new DOMParser().parseFromString(html, 'text/html');
  document.querySelectorAll('img[src]').forEach(image => {
    const source = image.getAttribute('src');
    const asset = useTemporaryUrl
      ? assets.find(item => source === item.marker)
      : assets.find(item => source === item.url);
    if (asset) {
      image.setAttribute('src', useTemporaryUrl ? asset.url : asset.marker);
      image.setAttribute('alt', asset.description || '');
    }
  });
  return document.body.innerHTML;
};

const mergeDefaults = (layout, values) => ({
  ...(layout?.templatemetadata?.defaults || {}),
  ...(values || {}),
});

export default function EmailTemplateForm() {
  const history = useHistory();
  const { lang, templateId } = useParams();
  const intl = useIntl();
  const ability = useContext(AbilityContext);
  const isEdit = Boolean(templateId);
  const canView = ability?.can(isEdit ? 'View' : 'Add', 'EmailCustomSettings');
  const canSave = ability?.can(isEdit ? 'Edit' : 'Add', 'EmailCustomSettings');

  const [template, setTemplate] = useState(EMPTY_TEMPLATE);
  const [layouts, setLayouts] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [htmlDiagnostics, setHtmlDiagnostics] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [layoutChanging, setLayoutChanging] = useState(false);
  const [layoutSkeletonVisible, setLayoutSkeletonVisible] = useState(false);
  const [layoutFieldsSnapshot, setLayoutFieldsSnapshot] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, variant: 'success', message: '' });
  const subjectRef = useRef(null);
  const quillRef = useRef(null);
  const htmlEditorRef = useRef(null);
  const imageInputRef = useRef(null);
  const diagramDraftsRef = useRef({});
  const customDraftRef = useRef(null);
  const selectedLayoutRef = useRef(DEFAULT_LAYOUT_ID);
  const previewSequenceRef = useRef(0);
  const immediatePreviewLayoutRef = useRef(null);
  const layoutTransitionRef = useRef({
    active: false,
    sequence: 0,
    layoutId: null,
    shownAt: 0,
    showTimer: null,
    hideTimer: null,
  });

  const message = (id, values) => intl.formatMessage({ id }, values);
  const placeholderGroupNames = {
    1: message('emailtemplates.placeholders.ticket'),
    2: message('emailtemplates.placeholders.portal'),
    3: message('emailtemplates.placeholders.company'),
    4: message('emailtemplates.placeholders.requester'),
    5: message('emailtemplates.placeholders.other'),
  };
  const showNotification = (variant, text) => setNotification({ open: true, variant, message: text });

  const beginLayoutTransition = (layoutId, snapshot) => {
    const previous = layoutTransitionRef.current;
    if (previous.showTimer) clearTimeout(previous.showTimer);
    if (previous.hideTimer) clearTimeout(previous.hideTimer);
    previewSequenceRef.current += 1;
    const sequence = previous.sequence + 1;
    const keepVisible = previous.active && previous.shownAt > 0;
    layoutTransitionRef.current = {
      active: true,
      sequence,
      layoutId: Number(layoutId),
      shownAt: keepVisible ? previous.shownAt : 0,
      showTimer: null,
      hideTimer: null,
    };
    immediatePreviewLayoutRef.current = Number(layoutId);
    setLayoutChanging(true);
    setLayoutSkeletonVisible(keepVisible);
    setLayoutFieldsSnapshot(current => previous.active && current ? current : snapshot);
    if (!keepVisible) {
      layoutTransitionRef.current.showTimer = setTimeout(() => {
        const transition = layoutTransitionRef.current;
        if (!transition.active || transition.sequence !== sequence) return;
        transition.shownAt = Date.now();
        transition.showTimer = null;
        setLayoutSkeletonVisible(true);
      }, 120);
    }
  };

  const finishLayoutTransition = sequence => {
    const transition = layoutTransitionRef.current;
    if (!transition.active || transition.sequence !== sequence) return;
    if (transition.showTimer) {
      clearTimeout(transition.showTimer);
      transition.showTimer = null;
    }
    const close = () => {
      const latest = layoutTransitionRef.current;
      if (!latest.active || latest.sequence !== sequence) return;
      layoutTransitionRef.current = {
        ...latest,
        active: false,
        shownAt: 0,
        showTimer: null,
        hideTimer: null,
      };
      setLayoutSkeletonVisible(false);
      setLayoutChanging(false);
      setLayoutFieldsSnapshot(null);
    };
    const visibleFor = transition.shownAt > 0 ? Date.now() - transition.shownAt : 0;
    const remaining = transition.shownAt > 0 ? Math.max(0, 300 - visibleFor) : 0;
    if (remaining > 0) {
      transition.hideTimer = setTimeout(close, remaining);
    } else {
      close();
    }
  };

  useEffect(() => () => {
    const transition = layoutTransitionRef.current;
    if (transition.showTimer) clearTimeout(transition.showTimer);
    if (transition.hideTimer) clearTimeout(transition.hideTimer);
  }, []);

  useEffect(() => {
    if (!canView) return;
    const load = async () => {
      setInitialLoading(true);
      try {
        const requests = [Service.getPlaceholders(), Service.getPredefinedLayouts()];
        if (isEdit) {
          requests.push(Service.getById(templateId));
          requests.push(Service.getAssets(templateId));
        }
        const [placeholderResult, layoutResult, templateResult, assetResult] = await Promise.all(requests);
        const loadedLayouts = layoutResult.success ? layoutResult.data || [] : [];
        setLayouts(loadedLayouts);
        setPlaceholders(placeholderResult.success ? placeholderResult.data || [] : []);
        if (!placeholderResult.success) showNotification('error', placeholderResult.message || message('emailtemplates.placeholders.error'));
        if (!layoutResult.success) showNotification('error', layoutResult.message || message('emailtemplates.layouts.load.error'));

        if (isEdit) {
          const loadedAssets = assetResult?.success ? assetResult.data || [] : [];
          setAssets(loadedAssets);
          if (!assetResult?.success) showNotification('error', assetResult?.message || message('emailtemplates.assets.load.error'));
          if (templateResult?.success && templateResult.data) {
            const loaded = templateResult.data;
            const mode = Number(loaded.layoutmode) || LAYOUT_MODE.predefined;
            const layoutId = Number(loaded.predefinedlayoutid) || DEFAULT_LAYOUT_ID;
            const selectedLayout = loadedLayouts.find(item => Number(item.id) === layoutId);
            const body = mode === LAYOUT_MODE.predefined
              ? replaceAssetSources(loaded.bodyhtml || '', loadedAssets, true)
              : loaded.bodyhtml || CUSTOM_HTML_START;
            const nextTemplate = {
              id: Number(loaded.id) || Number(templateId),
              name: loaded.name || '',
              subject: loaded.subject || '',
              bodyhtml: body,
              layoutmode: mode,
              predefinedlayoutid: mode === LAYOUT_MODE.predefined ? layoutId : null,
              layoutdata: mode === LAYOUT_MODE.predefined ? mergeDefaults(selectedLayout, loaded.layoutdata) : null,
              active: Boolean(loaded.active),
              isdraft: Boolean(loaded.isdraft),
            };
            setTemplate(nextTemplate);
            if (mode === LAYOUT_MODE.predefined) {
              selectedLayoutRef.current = layoutId;
              diagramDraftsRef.current[layoutId] = { bodyhtml: body, layoutdata: nextTemplate.layoutdata };
            } else {
              customDraftRef.current = body;
            }
          } else {
            showNotification('error', templateResult?.message || message('emailtemplates.load.one.error'));
          }
        } else {
          const defaultLayout = loadedLayouts.find(item => Number(item.id) === DEFAULT_LAYOUT_ID);
          const layoutdata = mergeDefaults(defaultLayout, null);
          setTemplate(current => ({ ...current, layoutdata }));
          diagramDraftsRef.current[DEFAULT_LAYOUT_ID] = { bodyhtml: SAMPLE_BODY, layoutdata };
        }
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [canView, isEdit, templateId]);

  const updateField = (field, value) => setTemplate(current => ({ ...current, [field]: value }));
  const selectedLayout = layouts.find(item => Number(item.id) === Number(template.predefinedlayoutid));
  const displayedLayout = layoutChanging && !layoutSkeletonVisible && layoutFieldsSnapshot?.layout
    ? layoutFieldsSnapshot.layout
    : selectedLayout;
  const displayedLayoutValues = layoutChanging && !layoutSkeletonVisible && layoutFieldsSnapshot
    ? layoutFieldsSnapshot.values
    : template.layoutdata;

  const rememberCurrentDraft = current => {
    if (Number(current.layoutmode) === LAYOUT_MODE.customHtml) {
      customDraftRef.current = current.bodyhtml;
      return;
    }
    if (current.predefinedlayoutid) {
      diagramDraftsRef.current[current.predefinedlayoutid] = {
        bodyhtml: current.bodyhtml,
        layoutdata: current.layoutdata,
      };
    }
  };

  const changeMode = mode => {
    setTemplate(current => {
      rememberCurrentDraft(current);
      if (Number(mode) === LAYOUT_MODE.customHtml) {
        return {
          ...current,
          layoutmode: LAYOUT_MODE.customHtml,
          predefinedlayoutid: null,
          layoutdata: null,
          bodyhtml: customDraftRef.current || CUSTOM_HTML_START,
        };
      }
      const layoutId = selectedLayoutRef.current || DEFAULT_LAYOUT_ID;
      const layout = layouts.find(item => Number(item.id) === Number(layoutId));
      const draft = diagramDraftsRef.current[layoutId];
      return {
        ...current,
        layoutmode: LAYOUT_MODE.predefined,
        predefinedlayoutid: layoutId,
        layoutdata: draft?.layoutdata || mergeDefaults(layout, null),
        bodyhtml: draft?.bodyhtml || SAMPLE_BODY,
      };
    });
  };

  const changeLayout = layoutId => {
    const normalizedLayoutId = Number(layoutId);
    if (normalizedLayoutId === Number(template.predefinedlayoutid)) return;
    beginLayoutTransition(normalizedLayoutId, {
      layout: selectedLayout,
      values: template.layoutdata,
    });
    setTemplate(current => {
      rememberCurrentDraft(current);
      selectedLayoutRef.current = normalizedLayoutId;
      const nextLayout = layouts.find(item => Number(item.id) === normalizedLayoutId);
      const draft = diagramDraftsRef.current[normalizedLayoutId];
      return {
        ...current,
        predefinedlayoutid: normalizedLayoutId,
        bodyhtml: draft?.bodyhtml || current.bodyhtml || SAMPLE_BODY,
        layoutdata: draft?.layoutdata || mergeDefaults(nextLayout, null),
      };
    });
  };

  const updateLayoutField = (key, value) => setTemplate(current => {
    if (current.layoutdata?.[key] === value) return current;
    return {
      ...current,
      layoutdata: { ...(current.layoutdata || {}), [key]: value },
    };
  });

  const resetLayout = () => {
    if (!selectedLayout) return;
    updateField('layoutdata', mergeDefaults(selectedLayout, null));
  };

  const payloadFor = current => ({
    id: Number(templateId || current.id || 0),
    name: current.name.trim(),
    subject: current.subject.trim(),
    bodyhtml: Number(current.layoutmode) === LAYOUT_MODE.predefined
      ? replaceAssetSources(current.bodyhtml, assets, false)
      : current.bodyhtml,
    layoutmode: Number(current.layoutmode),
    predefinedlayoutid: Number(current.layoutmode) === LAYOUT_MODE.predefined
      ? Number(current.predefinedlayoutid)
      : null,
    layoutdata: Number(current.layoutmode) === LAYOUT_MODE.predefined ? current.layoutdata : null,
    active: Boolean(current.active),
  });

  const requestPreview = async current => {
    const sequence = ++previewSequenceRef.current;
    const activeTransition = layoutTransitionRef.current;
    const transitionSequence = activeTransition.active
      && activeTransition.layoutId === Number(current.predefinedlayoutid)
      ? activeTransition.sequence
      : null;
    if (Number(current.layoutmode) === LAYOUT_MODE.customHtml) {
      const diagnostics = validateHtmlDocument(current.bodyhtml, message);
      setHtmlDiagnostics(diagnostics);
      if (diagnostics.length) {
        setPreviewError(diagnostics);
        setPreviewLoading(false);
        return;
      }
    }
    setPreviewLoading(true);
    const result = await Service.preview(payloadFor(current));
    if (sequence !== previewSequenceRef.current) return;
    if (result.success) {
      setPreview(result.data);
      setPreviewError('');
    } else {
      setPreviewError(result.message || message('emailtemplates.preview.error'));
    }
    setPreviewLoading(false);
    if (transitionSequence) finishLayoutTransition(transitionSequence);
  };

  useEffect(() => {
    if (initialLoading || !canView) return undefined;
    const layoutId = Number(template.predefinedlayoutid);
    const immediate = layoutTransitionRef.current.active
      && immediatePreviewLayoutRef.current === layoutId;
    if (immediate) immediatePreviewLayoutRef.current = null;
    const timer = setTimeout(() => requestPreview(template), immediate ? 0 : 520);
    return () => clearTimeout(timer);
  }, [template, assets, initialLoading, canView]);

  const validate = () => {
    if (!template.name.trim()) return message('emailtemplates.validation.name');
    if (template.name.trim().length > 150) return message('emailtemplates.validation.name.length');
    if (!template.subject.trim()) return message('emailtemplates.validation.subject');
    if (template.subject.length > 200) return message('emailtemplates.validation.subject.length');
    if (!template.bodyhtml || template.bodyhtml === '<p><br></p>') return message('emailtemplates.validation.body');
    if (template.bodyhtml.length > 100000) return message('emailtemplates.validation.body.length');
    if (Number(template.layoutmode) === LAYOUT_MODE.predefined && !template.predefinedlayoutid) return message('emailtemplates.validation.layout');
    if (Number(template.layoutmode) === LAYOUT_MODE.customHtml) {
      const diagnostics = validateHtmlDocument(template.bodyhtml, message);
      if (diagnostics.length) return diagnostics[0];
    }
    const supported = new Set(placeholders.map(item => item.name.toLowerCase()));
    const layoutText = template.layoutdata ? JSON.stringify(template.layoutdata) : '';
    const used = `${template.subject} ${template.bodyhtml} ${layoutText}`.match(/\{\{[^{}]+\}\}/g) || [];
    const unknown = used.find(item => !supported.has(item.toLowerCase()));
    if (unknown) return message('emailtemplates.validation.placeholder').replace('{placeholder}', unknown);
    return '';
  };

  const save = async () => {
    if (actionLoading) return;
    const validationMessage = validate();
    if (validationMessage) {
      showNotification('error', validationMessage);
      return;
    }
    setActionLoading(true);
    try {
      const payload = payloadFor(template);
      const effectiveTemplateId = Number(templateId || template.id || 0);
      const result = effectiveTemplateId
        ? await Service.update(effectiveTemplateId, payload)
        : await Service.create(payload);
      if (result.success) {
        setTemplate(current => ({
          ...current,
          id: Number(result.data?.id || current.id || templateId || 0),
          isdraft: false,
        }));
        showNotification('success', message('emailtemplates.save.success'));
        if (!isEdit && result.data?.id) history.replace(`/${lang}/email-template-edit/${result.data.id}`);
      } else {
        showNotification('error', result.message || message('emailtemplates.save.error'));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const insertAsset = asset => {
    if (!asset) return;
    if (Number(template.layoutmode) === LAYOUT_MODE.customHtml) {
      const marker = asset.marker || `mexx-email-asset://${asset.id}`;
      const alternativeText = escapeHtmlAttribute(asset.description || asset.name || 'Imagem');
      htmlEditorRef.current?.insertText(
        `<img src="${escapeHtmlAttribute(marker)}" alt="${alternativeText}" style="display:block;max-width:100%;height:auto;">`
      );
      return;
    }
    if (!asset.url || !quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const selection = editor.getSelection();
    const index = selection?.index ?? Math.max(0, editor.getLength() - 1);
    editor.insertEmbed(index, 'image', asset.url, 'user');
    editor.setSelection(index + 1, 0, 'user');
    editor.focus();
  };

  const uploadAsset = async event => {
    if (actionLoading) return;
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      showNotification('error', message('emailtemplates.assets.validation.type'));
      return;
    }
    if (file.size <= 0 || file.size > 5 * 1024 * 1024) {
      showNotification('error', message('emailtemplates.assets.validation.size'));
      return;
    }
    setActionLoading(true);
    try {
      let effectiveTemplateId = Number(templateId || template.id || 0);
      let createdDraft = false;
      if (!effectiveTemplateId) {
        const draftResult = await Service.createDraft(payloadFor(template));
        if (!draftResult.success || !draftResult.data?.id) {
          showNotification('error', draftResult.message || message('emailtemplates.draft.create.error'));
          return;
        }
        effectiveTemplateId = Number(draftResult.data.id);
        createdDraft = true;
        setTemplate(current => ({ ...current, id: effectiveTemplateId, isdraft: true }));
      } else if (template.isdraft) {
        const draftResult = await Service.updateDraft(effectiveTemplateId, payloadFor(template));
        if (!draftResult.success) {
          showNotification('error', draftResult.message || message('emailtemplates.draft.update.error'));
          return;
        }
      }

      const result = await Service.uploadAsset(effectiveTemplateId, file);
      if (result.success && result.data) {
        setAssets(current => [...current, result.data]);
        showNotification('success', message('emailtemplates.assets.upload.success'));
      } else {
        showNotification('error', result.message || message('emailtemplates.assets.upload.error'));
      }
      if (createdDraft) history.replace(`/${lang}/email-template-edit/${effectiveTemplateId}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (!canView) return <EmailSettingsLayout><Typography variant="h6">{message('emailsettings.forbidden')}</Typography></EmailSettingsLayout>;
  if (initialLoading) return <EmailSettingsLayout notification={notification} closeNotification={() => setNotification(current => ({ ...current, open: false }))}><EmailSettingsFormSkeleton sections={4} /></EmailSettingsLayout>;

  return (
    <EmailSettingsLayout notification={notification} closeNotification={() => setNotification(current => ({ ...current, open: false }))}>
      <EmailSettingsPageHeader
        icon={<EmailOutlinedIcon />}
        title={message(template.isdraft
          ? 'emailtemplates.draft.edit.title'
          : isEdit ? 'emailtemplates.edit.title' : 'emailtemplates.create.title')}
        subtitle={message('emailtemplates.form.subtitle')}
        actions={(
          <>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} disabled={actionLoading} onClick={() => history.push(`/${lang}/email-templates`)} sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444' }}>{message('emailsettings.back')}</Button>
            {canSave && <Button variant="contained" startIcon={actionLoading ? <CircularProgress size={17} color="inherit" /> : <SaveIcon />} disabled={actionLoading} onClick={save} sx={{ textTransform: 'none', backgroundColor: '#8BC34A', '&:hover': { backgroundColor: '#7cb342' } }}>{message('emailsettings.save')}</Button>}
          </>
        )}
      />

      <EmailSettingsPanel title={message('emailtemplates.section.basic')} subtitle={message('emailtemplates.section.basic.help')}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={9}>
            <TextField fullWidth required disabled={!canSave || actionLoading} label={message('emailtemplates.name')} value={template.name} inputProps={{ maxLength: 150 }} helperText={`${template.name.length}/150`} onChange={event => updateField('name', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel control={<Switch checked={template.active} disabled={!canSave || actionLoading} color="secondary" onChange={event => updateField('active', event.target.checked)} />} label={message('emailsettings.active')} />
          </Grid>
        </Grid>
      </EmailSettingsPanel>

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} lg={7}>
          <EmailSettingsPanel title={message('emailtemplates.mode.title')} subtitle={message('emailtemplates.mode.help')}>
            <LayoutModeSelector value={template.layoutmode} onChange={changeMode} disabled={!canSave || actionLoading || layoutChanging} message={message} />
            {Number(template.layoutmode) === LAYOUT_MODE.predefined && (
              <Box sx={{ mt: 2.5 }}><PredefinedLayoutGallery layouts={layouts} selectedId={template.predefinedlayoutid} onSelect={changeLayout} disabled={!canSave || actionLoading} language={lang || intl.locale} message={message} /></Box>
            )}
          </EmailSettingsPanel>

          <EmailSettingsPanel title={message('emailtemplates.section.content')} subtitle={message('emailtemplates.section.content.help')}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#555', mb: 1 }}>{message('emailtemplates.subject')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3 }}>
              <TextField fullWidth required disabled={!canSave || actionLoading} inputRef={subjectRef} value={template.subject} inputProps={{ maxLength: 200 }} helperText={`${template.subject.length}/200`} onChange={event => updateField('subject', event.target.value)} />
              {canSave && <EmailTemplatePlaceholder value={template.subject} onChange={value => updateField('subject', value)} textAreaRef={subjectRef} insertAtCursor placeholders={placeholders} groupNames={placeholderGroupNames} title={message('emailtemplates.placeholders')} searchPlaceholder={message('emailtemplates.placeholders.search')} disabled={actionLoading} />}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#555' }}>{message('emailtemplates.body')}</Typography>
              {Number(template.layoutmode) === LAYOUT_MODE.customHtml && canSave && (
                <EmailTemplatePlaceholder placeholders={placeholders} groupNames={placeholderGroupNames} title={message('emailtemplates.placeholders')} searchPlaceholder={message('emailtemplates.placeholders.search')} disabled={actionLoading} onInsert={code => htmlEditorRef.current?.insertText(code)} />
              )}
            </Box>

            {Number(template.layoutmode) === LAYOUT_MODE.predefined ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 280, '& .ql-toolbar': { borderRadius: '8px 8px 0 0', backgroundColor: '#fafafa' }, '& .ql-container': { borderRadius: '0 0 8px 8px', minHeight: 220 } }}>
                  <ReactQuill ref={quillRef} readOnly={!canSave || actionLoading} theme="snow" value={template.bodyhtml} modules={MODULES} formats={FORMATS} style={{ minHeight: 230 }} onChange={value => updateField('bodyhtml', value)} />
                </Box>
                {canSave && <EmailTemplatePlaceholder value={template.bodyhtml} onChange={value => updateField('bodyhtml', value)} quillRef={quillRef} placeholders={placeholders} groupNames={placeholderGroupNames} title={message('emailtemplates.placeholders')} searchPlaceholder={message('emailtemplates.placeholders.search')} disabled={actionLoading} />}
              </Box>
            ) : (
              <>
                <HtmlRequirements message={message} />
                {htmlDiagnostics.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
                    <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.4 }}>
                      {htmlDiagnostics.map(item => <li key={item}>{item}</li>)}
                    </Box>
                  </Alert>
                )}
                <HtmlCodeEditor ref={htmlEditorRef} value={template.bodyhtml} onChange={value => updateField('bodyhtml', value)} disabled={!canSave || actionLoading} message={message} onDiagnostics={setHtmlDiagnostics} />
              </>
            )}
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>{`${template.bodyhtml.length}/100000`}</Typography>
          </EmailSettingsPanel>

          {Number(template.layoutmode) === LAYOUT_MODE.predefined && (
            <EmailSettingsPanel title={message('emailtemplates.customization.title')} subtitle={message('emailtemplates.customization.help')}>
              <DynamicLayoutFields layout={displayedLayout} values={displayedLayoutValues} onChange={updateLayoutField} onReset={resetLayout} assets={assets} disabled={!canSave || actionLoading || layoutChanging} loading={layoutSkeletonVisible} language={lang || intl.locale} message={message} />
            </EmailSettingsPanel>
          )}

          <EmailSettingsPanel title={message('emailtemplates.assets.title')} subtitle={message('emailtemplates.assets.help')}>
            <input ref={imageInputRef} hidden type="file" disabled={!canSave || actionLoading} accept="image/png,image/jpeg,image/gif,image/webp" onChange={uploadAsset} />
            {canSave ? (
              <Button variant="outlined" startIcon={actionLoading ? <CircularProgress size={17} /> : <ImageIcon />} disabled={actionLoading} onClick={() => imageInputRef.current?.click()} sx={{ textTransform: 'none', mb: assets.length ? 2 : 0 }}>{message('emailtemplates.assets.upload')}</Button>
            ) : (
              <Typography variant="body2" color="textSecondary">{message('emailtemplates.assets.readonly')}</Typography>
            )}
            {template.isdraft && (
              <Alert severity="info" sx={{ mt: 1.5, mb: assets.length ? 1.5 : 0, fontSize: 12 }}>
                {message('emailtemplates.draft.upload.persistence.help')}
              </Alert>
            )}
            {assets.length > 0 && (
              <Grid container spacing={1.5}>
                {assets.map(asset => (
                  <Grid item xs={12} sm={6} md={4} key={asset.id}>
                    <Box sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: '10px', bgcolor: '#fff' }}>
                      <Box component="img" src={asset.url} alt={asset.description} sx={{ display: 'block', width: '100%', height: 86, objectFit: 'contain', mb: 0.75, borderRadius: '6px', bgcolor: '#fafafa' }} />
                      <Typography sx={{ fontSize: 11.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.75 }}>{asset.description}</Typography>
                      <Button fullWidth size="small" variant="outlined" startIcon={Number(template.layoutmode) === LAYOUT_MODE.customHtml ? <CodeIcon /> : <InsertPhotoOutlinedIcon />} disabled={!canSave || actionLoading} onClick={() => insertAsset(asset)} sx={{ textTransform: 'none', fontSize: 11 }}>
                        {message(Number(template.layoutmode) === LAYOUT_MODE.customHtml ? 'emailtemplates.assets.insert.marker' : 'emailtemplates.assets.insert.body')}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </EmailSettingsPanel>
        </Grid>

        <Grid item xs={12} lg={5} sx={{ alignSelf: { lg: 'stretch' } }}>
          <EmailDesignerPreview preview={preview} loading={previewLoading} layoutChanging={layoutSkeletonVisible} layoutUpdating={layoutChanging} error={previewError} onRefresh={() => requestPreview(template)} message={message} />
        </Grid>
      </Grid>
    </EmailSettingsLayout>
  );
}
